import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { App } from './App';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { GoogleReviewProvider } from './contexts/GoogleReviewContext';
import { MilestoneReviewProvider } from './contexts/MilestoneReviewContext';
import { GoogleReviewTrigger } from './components/ToxicDetector/GoogleReviewTrigger';
const GlobalModals = lazy(() => import('./components/GlobalModals').then(m => ({
  default: m.GlobalModals
})));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage').then(m => ({
  default: m.NotFoundPage
})));
const StaticHtmlPage = lazy(() => import('./pages/StaticHtmlPage').then(m => ({
  default: m.StaticHtmlPage
})));
import { mainRoutes } from './routes/mainRoutes';
import { protectedRoutes } from './routes/protectedRoutes';
// Known public paths that have static HTML in production but may not have explicit React routes
const PUBLIC_PREFIXES = ['/about', '/blog', '/faq', '/privacy', '/terms', '/es', '/fr', '/de', '/pt', '/ja', '/ko', '/hi', '/id'];
function CatchAllRoute() {
  const location = useLocation();
  const isPublic = PUBLIC_PREFIXES.some(prefix => location.pathname === prefix || location.pathname.startsWith(prefix + '/'));
  // For any public path not matched by explicit routes, render static HTML from /public
  if (isPublic) {
    return <StaticHtmlPage data-id="element-65" />;
  }
  return <NotFoundPage data-id="element-66" />;
}
function PageLoader() {
  return <div className="flex items-center justify-center min-h-screen bg-[#050505]" data-id="element-67">
      <div className="flex flex-col items-center gap-4" data-id="element-68">
        <div className="w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full animate-spin" data-id="element-69" />
        <span className="text-gray-400 text-sm font-mono tracking-wider" data-id="element-70">
          LOADING...
        </span>
      </div>
    </div>;
}
function AuthenticatedTriggers() {
  const {
    user
  } = useAuth();
  return <GoogleReviewTrigger userId={user?.id} data-id="element-71" />;
}
// Paths where we should NOT bounce a signed-in user even if they haven't onboarded.
// These are auth/payment flows, the onboarding screen itself, public content, etc.
const ONBOARDING_EXEMPT_PREFIXES = ['/onboarding', '/auth', '/login', '/signup', '/payment', '/loading', '/results', '/about', '/privacy', '/terms', '/faq', '/blog', '/test-google', '/debug', '/es', '/fr', '/de', '/pt', '/ja', '/ko', '/hi', '/id'];
function OnboardingRedirectGuard() {
  const {
    user,
    loading
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (loading || !user) return;
    const completed = (user.user_metadata as any)?.completedOnboarding;
    if (completed) return;
    const path = location.pathname;
    const isExempt = ONBOARDING_EXEMPT_PREFIXES.some(prefix => path === prefix || path.startsWith(prefix + '/'));
    if (isExempt) return;
    console.log('[OnboardingGuard] Redirecting un-onboarded user from', path, 'to /onboarding');
    navigate('/onboarding', {
      replace: true
    });
  }, [user, loading, location.pathname, navigate]);
  return null;
}
export function AppRouter() {
  return <BrowserRouter data-id="element-72">
      <AuthProvider data-id="element-73">
        <ToastProvider data-id="element-74">
          <GoogleReviewProvider data-id="element-75">
            <MilestoneReviewProvider data-id="element-76">
              <AuthenticatedTriggers data-id="element-77" />
              <OnboardingRedirectGuard data-id="element-78" />
              <Suspense fallback={null} data-id="element-79">
                <GlobalModals data-id="element-80" />
              </Suspense>
              <Suspense fallback={<PageLoader data-id="element-82" />} data-id="element-81">
                <Routes data-id="element-83">
                  <Route path="/" element={<App data-id="element-85" />} data-id="element-84" />
                  {mainRoutes}
                  {protectedRoutes}
                  <Route path="*" element={<CatchAllRoute data-id="element-87" />} data-id="element-86" />
                </Routes>
              </Suspense>
            </MilestoneReviewProvider>
          </GoogleReviewProvider>
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>;
}